import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_ENDPOINTS from '../../config/api';
import GalleryImagePicker from './GalleryImagePicker';
import { PUBLIC_MODULES, MODULE_GROUPS } from '../../config/publicModules';
import { defaultModuleSettings } from '../../utils/moduleVisibility';
import { GUEST_SECTION_DEFAULTS } from '../../config/guestSection';
import { INFO_CARDS_DEFAULTS } from '../../config/infoCards';
import { SHARE_LINK_DEFAULTS } from '../../config/shareLink';
import { getShareDescription, getShareTitle } from '../../utils/siteMeta';
import { FONT_ASSIGNMENTS } from '../../utils/siteFonts';
import FontSettings from './FontSettings';

const LEGACY_SYNC = {
  module_gallery_visible: 'enable_gallery',
  module_gifts_visible: 'enable_gifts',
  module_accommodation_visible: 'enable_airbnb',
};

const WebsiteSettings = () => {
  const [settings, setSettings] = useState({
    wedding_title: '', couple_names: '', wedding_date: '', footer_message: '',
    ...SHARE_LINK_DEFAULTS,
    theme_primary_color: '#e91e63', theme_secondary_color: '#ff9800',
    ...defaultModuleSettings(),
    pledge_min_amount: '1000', pledge_currency: 'KES',
    pledge_bank_details: '', pledge_contact_info: '',
    pledge_couple_image: '', pledge_card_image: '',
    pledge_intro_text: '', pledge_email_from: '', pledge_email_subject: '',
    pledge_reminder_enabled: '1', pledge_reminder_subject: '',
    ...GUEST_SECTION_DEFAULTS,
    ...INFO_CARDS_DEFAULTS,
    ...Object.fromEntries(FONT_ASSIGNMENTS.map(({ key, default: d }) => [key, d])),
    program_timeline_title: 'Wedding\nTimeline',
    program_timeline_couple_names: '',
    program_timeline_date: '',
    program_show_timestamps: '1',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncingShare, setSyncingShare] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.GET_SETTINGS);
      if (response.data.success) {
        setSettings((prev) => ({ ...defaultModuleSettings(), ...prev, ...response.data.settings }));
      }
    } catch (error) { console.error('Error fetching settings:', error);
    } finally { setLoading(false); }
  };

  const setModuleVisible = (settingKey, checked) => {
    const value = checked ? '1' : '0';
    setSettings((prev) => {
      const next = { ...prev, [settingKey]: value };
      const legacyKey = LEGACY_SYNC[settingKey];
      if (legacyKey) next[legacyKey] = value;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.post(API_ENDPOINTS.ADMIN_SETTINGS, { settings });
      const patched = res.data?.share_meta_updated?.length;
      if (patched) {
        alert(`Settings saved. Share link preview updated (${res.data.share_meta_title}).`);
      } else {
        alert('Settings saved. Share preview could not update index.html on the server — use Apply share preview below.');
      }
    } catch (error) { alert('Error saving settings');
    } finally { setSaving(false); }
  };

  const handleSyncShareMeta = async () => {
    setSyncingShare(true);
    try {
      const res = await axios.post(API_ENDPOINTS.ADMIN_SYNC_SHARE_META);
      if (res.data?.success && res.data.updated_files?.length) {
        alert(`Share preview applied: ${res.data.title}`);
      } else {
        alert(res.data?.message || 'Could not update the live site index.html. Check server file permissions.');
      }
    } catch (error) {
      alert('Could not apply share preview to the live site.');
    } finally {
      setSyncingShare(false);
    }
  };

  if (loading) return <div className="loading">Loading settings...</div>;

  return (
    <div className="settings">
      <h1>Website Settings</h1>
      <div className="settings-form">
        <div className="form-group">
          <label>Couple Names</label>
          <input type="text" value={settings.couple_names} onChange={(e) => setSettings({...settings, couple_names: e.target.value})} />
        </div>
        <div className="form-group">
          <label>Wedding Date</label>
          <input type="date" value={settings.wedding_date} onChange={(e) => setSettings({...settings, wedding_date: e.target.value})} />
        </div>
        <div className="form-group">
          <label>Site title (footer)</label>
          <input
            type="text"
            value={settings.wedding_title}
            onChange={(e) => setSettings({ ...settings, wedding_title: e.target.value })}
            placeholder="Optional — shown in the site footer"
          />
        </div>

        <h2 className="settings-section-title">Link sharing (WhatsApp, Facebook, iMessage)</h2>
        <p className="settings-section-hint">
          Controls the title, description, and image when someone shares your wedding link. Click Save Settings at the bottom when you change these.
        </p>
        <div className="guest-message-admin-preview share-link-admin-preview">
          <p className="guest-message-admin-preview-label">Share preview</p>
          <p className="share-link-preview-title">{getShareTitle(settings)}</p>
          <p className="share-link-preview-description">{getShareDescription(settings)}</p>
        </div>
        <div className="form-group">
          <label>Share link title</label>
          <input
            type="text"
            value={settings.share_link_title ?? ''}
            onChange={(e) => setSettings({ ...settings, share_link_title: e.target.value })}
            placeholder={SHARE_LINK_DEFAULTS.share_link_title}
          />
        </div>
        <div className="form-group">
          <label>Share link description</label>
          <textarea
            value={settings.share_link_description ?? ''}
            onChange={(e) => setSettings({ ...settings, share_link_description: e.target.value })}
            placeholder={SHARE_LINK_DEFAULTS.share_link_description}
            rows={3}
          />
        </div>
        <div className="form-group">
          <label>Share preview image (optional)</label>
          <p className="form-hint">Used when the link is shared. Leave empty to use the default site image.</p>
          <GalleryImagePicker
            value={settings.share_link_image}
            onChange={(url) => setSettings((s) => ({ ...s, share_link_image: url }))}
          />
        </div>
        <div className="form-group">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleSyncShareMeta}
            disabled={syncingShare || saving}
          >
            {syncingShare ? 'Applying…' : 'Apply share preview to live site'}
          </button>
          <p className="form-hint">
            WhatsApp reads the static <strong>index.html</strong> at your site root — not the React app alone.
            After <code>npm run build</code>, upload everything inside <code>frontend/build/</code> to the site root
            (not into a <code>/build</code> subfolder). Then click this button. WhatsApp may cache the old preview for
            a while; delete the chat message and paste the link again, or wait a few hours.
          </p>
        </div>

        <h2 className="settings-section-title">Message to guests</h2>
        <p className="settings-section-hint">
          Edits the &ldquo;TO OUR GUESTS&rdquo; block on the public homepage (between the hero and the guest cards). Click <strong>Save Settings</strong> at the bottom when done.
        </p>
        <div className="form-group">
          <label className="module-toggle-label">
            <input
              type="checkbox"
              checked={(settings.module_guest_message_visible ?? '1') === '1'}
              onChange={(e) => setModuleVisible('module_guest_message_visible', e.target.checked)}
            />
            {' '}Show this section on the public site
          </label>
        </div>
        <div className="guest-message-admin-preview">
          <p className="guest-message-admin-preview-label">Public preview</p>
          <p className="guest-message-admin-preview-title">
            {settings.guest_section_title?.trim() || GUEST_SECTION_DEFAULTS.guest_section_title}
          </p>
          <p className="guest-message-admin-preview-body">
            {settings.guest_section_body?.trim() || GUEST_SECTION_DEFAULTS.guest_section_body}
          </p>
        </div>
        <div className="form-group">
          <label>Section title (e.g. TO OUR GUESTS)</label>
          <input
            type="text"
            value={settings.guest_section_title ?? ''}
            onChange={(e) => setSettings({ ...settings, guest_section_title: e.target.value })}
            placeholder={GUEST_SECTION_DEFAULTS.guest_section_title}
          />
        </div>
        <div className="form-group">
          <label>Welcome message</label>
          <textarea
            value={settings.guest_section_body ?? ''}
            onChange={(e) => setSettings({ ...settings, guest_section_body: e.target.value })}
            rows="5"
            placeholder={GUEST_SECTION_DEFAULTS.guest_section_body}
          />
        </div>

        <h2 className="settings-section-title">Gifting &amp; dress code cards</h2>
        <p className="settings-section-hint">
          Two info cards on the homepage (between the guest message and Plan your visit). Leave a field empty to use the default text.
        </p>
        <div className="form-group">
          <label className="module-toggle-label">
            <input
              type="checkbox"
              checked={(settings.module_info_cards_visible ?? '1') === '1'}
              onChange={(e) => setModuleVisible('module_info_cards_visible', e.target.checked)}
            />
            {' '}Show gifting &amp; dress code cards on the public site
          </label>
        </div>

        <h3 className="settings-subsection-title">Gifting with Love</h3>
        <div className="guest-message-admin-preview share-link-admin-preview">
          <p className="guest-message-admin-preview-label">Preview</p>
          <p className="share-link-preview-title">
            {settings.gifting_card_title?.trim() || INFO_CARDS_DEFAULTS.gifting_card_title}
          </p>
          <p className="share-link-preview-description">
            {(settings.gifting_card_body?.trim() || INFO_CARDS_DEFAULTS.gifting_card_body).slice(0, 180)}…
          </p>
        </div>
        <div className="form-group">
          <label>Card title</label>
          <input
            type="text"
            value={settings.gifting_card_title ?? ''}
            onChange={(e) => setSettings({ ...settings, gifting_card_title: e.target.value })}
            placeholder={INFO_CARDS_DEFAULTS.gifting_card_title}
          />
        </div>
        <div className="form-group">
          <label>Card message</label>
          <p className="form-hint">Separate paragraphs with a blank line.</p>
          <textarea
            value={settings.gifting_card_body ?? ''}
            onChange={(e) => setSettings({ ...settings, gifting_card_body: e.target.value })}
            rows={6}
            placeholder={INFO_CARDS_DEFAULTS.gifting_card_body}
          />
        </div>
        <div className="form-group">
          <label>Contact (phone / name)</label>
          <input
            type="text"
            value={settings.gifting_card_contact ?? ''}
            onChange={(e) => setSettings({ ...settings, gifting_card_contact: e.target.value })}
            placeholder={INFO_CARDS_DEFAULTS.gifting_card_contact}
          />
        </div>

        <h3 className="settings-subsection-title">Dress Code</h3>
        <div className="guest-message-admin-preview share-link-admin-preview">
          <p className="guest-message-admin-preview-label">Preview</p>
          <p className="share-link-preview-title">
            {settings.dress_code_card_title?.trim() || INFO_CARDS_DEFAULTS.dress_code_card_title}
          </p>
          <p className="share-link-preview-description">
            {(settings.dress_code_card_body?.trim() || INFO_CARDS_DEFAULTS.dress_code_card_body).slice(0, 180)}…
          </p>
        </div>
        <div className="form-group">
          <label>Card title</label>
          <input
            type="text"
            value={settings.dress_code_card_title ?? ''}
            onChange={(e) => setSettings({ ...settings, dress_code_card_title: e.target.value })}
            placeholder={INFO_CARDS_DEFAULTS.dress_code_card_title}
          />
        </div>
        <div className="form-group">
          <label>Card message</label>
          <p className="form-hint">Separate paragraphs with a blank line.</p>
          <textarea
            value={settings.dress_code_card_body ?? ''}
            onChange={(e) => setSettings({ ...settings, dress_code_card_body: e.target.value })}
            rows={8}
            placeholder={INFO_CARDS_DEFAULTS.dress_code_card_body}
          />
        </div>

        <h2 className="settings-section-title">Wedding Program / Timeline</h2>
        <p className="settings-section-hint">
          Configure the title, date, couple names, and timestamps displayed in the Wedding Program / Timeline modal.
        </p>
        <div className="form-group">
          <label>Timeline Card Title</label>
          <p className="form-hint">Use newlines to control line breaks on the card.</p>
          <textarea
            value={settings.program_timeline_title ?? ''}
            onChange={(e) => setSettings({ ...settings, program_timeline_title: e.target.value })}
            placeholder="Wedding&#10;Timeline"
            rows="2"
          />
        </div>
        <div className="form-group">
          <label>Timeline Card Subtitle / Couple Names</label>
          <input
            type="text"
            value={settings.program_timeline_couple_names ?? ''}
            onChange={(e) => setSettings({ ...settings, program_timeline_couple_names: e.target.value })}
            placeholder={settings.couple_names || 'Ursula & Vitalis'}
          />
        </div>
        <div className="form-group">
          <label>Timeline Card Date Override</label>
          <p className="form-hint">Leave blank to automatically format each event day's date (e.g. 10.31.26).</p>
          <input
            type="text"
            value={settings.program_timeline_date ?? ''}
            onChange={(e) => setSettings({ ...settings, program_timeline_date: e.target.value })}
            placeholder="e.g. 10.31.26"
          />
        </div>
        <div className="form-group">
          <label className="module-toggle-label">
            <input
              type="checkbox"
              checked={(settings.program_show_timestamps ?? '1') === '1'}
              onChange={(e) => setSettings({ ...settings, program_show_timestamps: e.target.checked ? '1' : '0' })}
            />
            {' '}Show time stamps on schedule items
          </label>
        </div>

        <div className="form-group">
          <label>Footer Message</label>
          <textarea value={settings.footer_message} onChange={(e) => setSettings({...settings, footer_message: e.target.value})} rows="3" />
        </div>
        <div className="form-group">
          <label>Primary Color</label>
          <input type="color" value={settings.theme_primary_color} onChange={(e) => setSettings({...settings, theme_primary_color: e.target.value})} />
        </div>
        <div className="form-group">
          <label>Secondary Color</label>
          <input type="color" value={settings.theme_secondary_color} onChange={(e) => setSettings({...settings, theme_secondary_color: e.target.value})} />
        </div>

        <FontSettings
          settings={settings}
          onSettingsChange={(key, value) => setSettings((prev) => ({ ...prev, [key]: value }))}
        />

        <h2 className="settings-section-title">Public site modules</h2>
        <p className="settings-section-hint">
          Control what guests see on the wedding website. Turn a module off to hide it from the homepage, navigation, and footer.
          Guest cards (RSVP, Pledge, etc.) stay available when at least one guest module is on — turning off &ldquo;Plan your visit&rdquo; only hides that section title, not the cards.
          Pledge also appears inside Gift registry when both Pledge and Gifts are enabled.
        </p>
        {MODULE_GROUPS.map((group) => (
          <div key={group.id} className="module-toggle-group">
            <h3 className="module-toggle-group-title">{group.title}</h3>
            <ul className="module-toggle-list">
              {PUBLIC_MODULES.filter((m) => m.group === group.id).map((mod) => (
                <li key={mod.id} className="module-toggle-item">
                  <label className="module-toggle-label">
                    <input
                      type="checkbox"
                      checked={(settings[mod.settingKey] ?? '1') === '1'}
                      onChange={(e) => setModuleVisible(mod.settingKey, e.target.checked)}
                    />
                    <span className="module-toggle-text">
                      <strong>{mod.label}</strong>
                      <span className="module-toggle-desc">{mod.description}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <h2 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Pledge Card</h2>
        <div className="form-group">
          <label>Minimum pledge amount</label>
          <input type="number" value={settings.pledge_min_amount} onChange={(e) => setSettings({...settings, pledge_min_amount: e.target.value})} />
        </div>
        <div className="form-group">
          <label>Default currency (guests can also choose KES, UGX, or USD)</label>
          <select
            value={settings.pledge_currency || 'KES'}
            onChange={(e) => setSettings({ ...settings, pledge_currency: e.target.value })}
          >
            <option value="KES">KES — Kenya Shilling</option>
            <option value="UGX">UGX — Uganda Shilling</option>
            <option value="USD">USD — US Dollar ($)</option>
          </select>
        </div>
        <div className="form-group">
          <label>Bank / payment details (shown on card)</label>
          <input type="text" value={settings.pledge_bank_details} onChange={(e) => setSettings({...settings, pledge_bank_details: e.target.value})} placeholder="StanChart - 0100823396100" />
        </div>
        <div className="form-group">
          <label>Contact info (shown on card)</label>
          <input type="text" value={settings.pledge_contact_info} onChange={(e) => setSettings({...settings, pledge_contact_info: e.target.value})} />
        </div>
        <div className="form-group">
          <label>Pledge page intro</label>
          <textarea value={settings.pledge_intro_text} onChange={(e) => setSettings({...settings, pledge_intro_text: e.target.value})} rows="2" />
        </div>
        <div className="form-group">
          <label>Pledge page header photo (optional)</label>
          <p style={{ fontSize: '0.85rem', color: '#666', margin: '0 0 0.5rem' }}>
            Choose from your gallery. Used as the background and avatar on the pledge page.
          </p>
          <GalleryImagePicker
            value={settings.pledge_couple_image}
            onChange={(url) => setSettings((s) => ({ ...s, pledge_couple_image: url }))}
          />
        </div>
        <div className="form-group">
          <label>Pledge card image (form preview & downloadable card)</label>
          <p style={{ fontSize: '0.85rem', color: '#666', margin: '0 0 0.5rem' }}>
            Choose from your gallery. Shown on the left side of the pledge card while guests fill the form and on the downloaded card.
          </p>
          <GalleryImagePicker
            value={settings.pledge_card_image}
            onChange={(url) => setSettings((s) => ({ ...s, pledge_card_image: url }))}
          />
        </div>
        <div className="form-group">
          <label>Confirmation email from address</label>
          <input type="email" value={settings.pledge_email_from} onChange={(e) => setSettings({...settings, pledge_email_from: e.target.value})} placeholder="noreply@yourdomain.com" />
        </div>
        <div className="form-group">
          <label>Confirmation email subject</label>
          <input type="text" value={settings.pledge_email_subject} onChange={(e) => setSettings({...settings, pledge_email_subject: e.target.value})} />
        </div>

        <h3 style={{ marginTop: '1.5rem' }}>Automatic reminders</h3>
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={settings.pledge_reminder_enabled === '1'}
              onChange={(e) => setSettings({ ...settings, pledge_reminder_enabled: e.target.checked ? '1' : '0' })}
            />
            {' '}Enable automatic pledge reminder emails
          </label>
        </div>
        <div className="form-group">
          <label>Reminder email subject</label>
          <input type="text" value={settings.pledge_reminder_subject} onChange={(e) => setSettings({...settings, pledge_reminder_subject: e.target.value})} placeholder="Reminder: Your Wedding Pledge" />
        </div>
        <p style={{ fontSize: '0.85rem', color: '#666', margin: '0.5rem 0 0' }}>
          To send reminders manually, open <strong>Cash Pledges</strong> and click &ldquo;Send due reminders now&rdquo;.
        </p>

        <button className="btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</button>
      </div>
    </div>
  );
};

export default WebsiteSettings;
